import { PropsWithChildren, useEffect } from 'react';
import { Table } from '@tanstack/react-table';
import { MdClose } from 'react-icons/md';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import DataTableFacetedFilter, {
  DataTableFacetedFilterOption,
} from './DataTableFacetedFilter';
import { useDebouncedState } from '@/hooks/useDebounce';

export type ToolBarFilter = {
  columnId: string;
  title: string;
  options: DataTableFacetedFilterOption[];
};

interface IProps<TData> extends PropsWithChildren {
  table: Table<TData>;
  className?: string;
  searchClassName?: string;
  searchPlaceholder?: string;
  searchColumnId?: string;
  filters?: ToolBarFilter[];
}

const DataTableToolbar = <TData,>({
  table,
  className,
  searchClassName,
  searchPlaceholder,
  searchColumnId,
  filters,
  children,
}: IProps<TData>) => {
  const isFiltered = table.getState().columnFilters.length > 0;
  const [debouncedValue, setValue, value] = useDebouncedState(
    searchColumnId
      ? ((table.getColumn(searchColumnId)?.getFilterValue() as string) ?? '')
      : '',
    300,
  );

  useEffect(() => {
    if (!searchColumnId) return;

    table.getColumn(searchColumnId)?.setFilterValue(debouncedValue);
  }, [debouncedValue, searchColumnId, table]);

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-wrap items-center gap-2',
        className,
      )}
    >
      {searchColumnId && (
        <Input
          placeholder={searchPlaceholder ?? 'Search...'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className={cn(
            'h-8 w-full sm:w-[150px] lg:w-[250px]',
            searchClassName,
          )}
        />
      )}
      {filters?.map(
        ({ columnId, title, options }) =>
          table.getColumn(columnId) && (
            <DataTableFacetedFilter
              key={columnId}
              column={table.getColumn(columnId)}
              title={title}
              options={options}
            />
          ),
      )}
      {children}
      {isFiltered && (
        <Button
          variant="ghost"
          onClick={() => table.resetColumnFilters()}
          className="h-8 px-2 lg:px-3"
        >
          Limpar
          <MdClose className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default DataTableToolbar;
